import React from 'react';
import { motion } from 'framer-motion';
import { Bot, Megaphone, Radar, ShieldCheck, Waves } from 'lucide-react';

const AMAIA_CAPABILITIES = [
  {
    icon: Bot,
    title: 'Guild agents',
    description: 'Dedicated AMAIA agents run inside each community to coordinate operations, growth, and safety.',
    accent: '#3BFF7A'
  },
  {
    icon: Megaphone,
    title: 'Announce + relay',
    description: 'Publish announcements with verifiable delivery, budgets, and real‑time relay feedback.',
    accent: '#0EF37F'
  },
  {
    icon: Radar,
    title: 'Signal intelligence',
    description: 'AMAIA elevates high‑signal activity and routes it to the right channels and people.',
    accent: '#8BFFAD'
  },
  {
    icon: ShieldCheck,
    title: 'Reputation‑aware',
    description: 'Actions are tied to soulbound identity so trust and risk are measurable, not assumed.',
    accent: '#6BFFB0'
  }
];

export const AmaiaSection: React.FC = () => {
  return (
    <section id="amaia" className="py-32 px-6 relative z-10">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[900px] h-[360px] bg-gradient-to-r from-transparent via-[#00ff9d]/8 to-transparent blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="inline-flex items-center gap-3 mb-6 px-4 py-2 rounded-full bg-white/[0.03] border border-green-500/20 backdrop-blur-md shadow-[0_0_15px_rgba(0,255,157,0.05)]">
              <Waves className="w-3.5 h-3.5 text-green-400" />
              <span className="text-[11px] uppercase tracking-[0.25em] font-medium text-green-300">
                AMAIA layer
              </span>
            </div>

            <h2
              className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter text-transparent bg-clip-text bg-gradient-to-br from-white via-white to-white/60"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              AMAIA powers
              <br className="hidden md:block" /> autonomous community ops
            </h2>

            <p className="mt-6 text-lg md:text-xl text-white/60 leading-relaxed max-w-xl font-light">
              AMAIA is your AI operations layer for Discord communities. It moderates with context,
              coordinates announcements, and routes verified signals to the right people—backed by
              soulbound identity.
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-4 text-sm text-white/70">
              <div className="border-l border-green-500/30 pl-4">
                <div className="text-xs uppercase tracking-[0.3em] text-green-200/70">Moderation</div>
                <div className="mt-2">Contextual, identity‑aware enforcement</div>
              </div>
              <div className="border-l border-green-500/30 pl-4">
                <div className="text-xs uppercase tracking-[0.3em] text-green-200/70">Relay</div>
                <div className="mt-2">Live announce queue and engagement stream</div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="grid sm:grid-cols-2 gap-5"
          >
            {AMAIA_CAPABILITIES.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="group relative bg-black/35 border border-white/10 rounded-3xl p-6 backdrop-blur-xl overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-black/20" />
                  <div
                    className="absolute -top-10 -right-10 w-32 h-32 rounded-full blur-[60px] opacity-0 group-hover:opacity-30 transition-opacity duration-500"
                    style={{ background: item.accent }}
                  />
                  <div className="relative z-10">
                    <div
                      className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5"
                      style={{
                        background: `${item.accent}20`,
                        border: `1px solid ${item.accent}40`,
                        color: item.accent
                      }}
                    >
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                    <p className="text-sm text-white/60 leading-relaxed">{item.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
